import React from 'react';
import moment from "moment";
import _ from "lodash";

import { connect } from 'react-redux';

import { Grid } from '@material-ui/core';

import NonFieldErrors from '../../../../../components/AssetModal/components/NonFieldErrors';
import {decimalStrToNumber} from "../../../../../utils/utils";
import SelectFormElementV2
  from "../../../../RiskProfiling/components/StepContent/components/formElement/SelectFormElementV2";
import InputFormElement
  , {CurrencyNumberFormat} from "../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement";
import CalendarFormElement
  from "../../../../RiskProfiling/components/StepContent/components/formElement/CalendarFormElement";
import {newDesignInputCustomClasses} from "../../../../RiskProfiling/constants";
import BaseNumberFormat from "../../../../../components/Inputs/BaseNumberFormat";
import {REAL_ESTATE_USAGE_TYPES} from "../../../../../components/AssetModal/constants";
import {choiceValue, dateToMoment, getCalculationDisabledReason, momentToDate} from "../../utils";
import {newDesignInputCustomClassesViewOnly, REAL_ESTATE_CATEGORY} from "../../../constants";
import {DECIMAL_SCALE} from "../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement/InputFormElement";
import {displayWarningSnackBar} from "../../../../../components/SnackbarProvider/actions";
import AutoCalculateToggle from "../AutoCalculateToggle";
import useModelWithAutoCalculatedField from "../hooks/useModelWithAutoCalculatedField";

const CONDITION_OPTIONS = [{
  id: 1,
  value: '1',
  label: 'Neu: Neubau, Erstbezug, keine Abnutzung / Schäden'
}, {
  id: 2,
  value: '2',
  label: 'Neuwertig: Kaum genutzt, keine Instandhaltungsrückstände'
}, {
  id: 3,
  value: '3',
  label: 'Gebraucht / Gepflegt: Normale Nutzung, laufend instandgehalten, vollfunktionsfähig'
}, {
  id: 4,
  value: '4',
  label: 'Instandhaltungsrückständig: Teilweise Reparaturbedarf, kleinere Mängel'
}, {
  id: 5,
  value: '5',
  label: 'Renovierungsbedürftig: Deutliche Abnutzungen, Instandsetzung erforderlich'
}, {
  id: 6,
  value: '6',
  label: 'Sanierungsbedürftig: Größere technische oder bauliche Mängel, Wert beeinträchtigt'
}, {
  id: 7,
  value: '7',
  label: 'Abbruchreif: Wirtschaftlich nicht mehr nutzbar, Abriss vorgesehen'
}, {
  id: 8,
  value: '8',
  label: 'Sonstiges'
}];

const USAGE_OPTIONS = REAL_ESTATE_USAGE_TYPES.map(o => ({...o, value: String(o.id)}));

export const PAYMENT_FREQUENCY = {
  monthly: {
    id: 1,
    value: '1',
    label: 'Monatlich'
  },
  quarterly: {
    id: 2,
    value: '2',
    label: 'Vierteljährlich'
  },
  half_yearly: {
    id: 3,
    value: '3',
    label: 'Halbjährlich'
  },
  yearly: {
    id: 4,
    value: '4',
    label: 'Jährlich'
  }
};

const PAYMENT_FREQUENCY_OPTIONS = Object.values(PAYMENT_FREQUENCY);

const PAYMENTS_PER_YEAR = {
  [PAYMENT_FREQUENCY.monthly.value]: 12,
  [PAYMENT_FREQUENCY.quarterly.value]: 4,
  [PAYMENT_FREQUENCY.half_yearly.value]: 2,
  [PAYMENT_FREQUENCY.yearly.value]: 1,
};

export const SquareSizeNumberFormat = (props) => {
  return (
    <BaseNumberFormat
      {...props}
      decimalScale={DECIMAL_SCALE}
      suffix={" m²"}
      placeholder={props.placeholder ? props.placeholder : '0,00 m²'}
    />
  );
};

const realEstateAutoCalculationHelper = {
  fieldName: 'rental_income',
  getInitialModel: (defaults) => ({
    estate_type: choiceValue(defaults.estate_type || defaults.sub_category),
    name: defaults.name,
    address: defaults.address,
    value: decimalStrToNumber(defaults.value),
    valuation_date: dateToMoment(defaults.valuation_date),
    purchase_price: decimalStrToNumber(defaults.purchase_price),
    acquisition_date: dateToMoment(defaults.acquisition_date),
    date_of_construction: dateToMoment(defaults.date_of_construction),
    size: defaults.size,
    usage: choiceValue(defaults.usage),
    payment_frequency: choiceValue(defaults.payment_frequency),
    net_income: defaults.net_income,
    rental_income: decimalStrToNumber(defaults.rental_income),
    condition: choiceValue(defaults.condition),
    updated: dateToMoment(defaults.updated),
  }),
  depsFields: ['net_income', 'payment_frequency', 'usage'],
  isAutoCalcAvailable: (model) => model.net_income && model.payment_frequency,
  calculateFieldValue: (model) => _.round(PAYMENTS_PER_YEAR[model.payment_frequency] * model.net_income, 2),
  getCalculationDeps: (model) => [model.net_income, model.payment_frequency],
};

const PAYMENT_FREQ_LABEL = 'Zahlungsrhythmus';
const NET_INCOME_LABEL = 'Nettoeinkünfte';
const AUTO_CALC_DISABLED_INFO = getCalculationDisabledReason([PAYMENT_FREQ_LABEL, NET_INCOME_LABEL]);

const ImmobilienForm = (props) => {

  const {
    dispatch,
    handleFormChange,
    defaults,
    errors,
    viewOnly
  } = props;

  const [model, handleModelChange, rentalIncome, isAutoCalcAvailable] = useModelWithAutoCalculatedField(
    realEstateAutoCalculationHelper, defaults
  );

  // "1" - Eigennutzung, "9" - Leerstehend, "10" - Unbebaut / in Entwicklung
  const isUsageHasNoIncome = ["1", "9", "10"].includes(model.usage);

  const currentPaymentFrequency = isUsageHasNoIncome ? null : model.payment_frequency;
  const currentNetIncome = isUsageHasNoIncome ? null : model.net_income;
  const currentRentalIncome = isUsageHasNoIncome ? null : rentalIncome;

  React.useEffect(() => {
    // data in UI and for BE might be not sync - cause UI could re-store (disabled) data
    handleFormChange && handleFormChange({
      ...model,
      payment_frequency: currentPaymentFrequency,
      net_income: currentNetIncome,
      rental_income: currentRentalIncome,
      valuation_date: momentToDate(model.valuation_date),
      acquisition_date: momentToDate(model.acquisition_date),
      date_of_construction: momentToDate(model.date_of_construction),
    });
  }, [model, currentRentalIncome, currentPaymentFrequency, currentNetIncome]);

  React.useEffect(() => {
    if(!viewOnly && isUsageHasNoIncome && rentalIncome){
      dispatch(displayWarningSnackBar('Werte gelöscht: Für diese Nutzungsart fallen keine Mieteinkünfte an.'))
    }
  }, [isUsageHasNoIncome, viewOnly, rentalIncome]);

  const inputClasses = viewOnly ? newDesignInputCustomClassesViewOnly : newDesignInputCustomClasses;

  return (
    <>
      {!viewOnly && (
        <Grid item xs={12}>
          <SelectFormElementV2
            label="Kategorie"
            dataId={'category-input'}
            value={model.estate_type}
            options={REAL_ESTATE_CATEGORY.subCategories}
            onChange={handleModelChange('estate_type')}
            required
            newDesign
            disabled={viewOnly}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <InputFormElement
          label={'Name / Bezeichnung'}
          required={!viewOnly}
          value={model.name}
          error={errors.name}
          dataId={'name-input'}
          onChange={handleModelChange('name')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <InputFormElement
          label={'Adresse / Flurstück'}
          value={model.address}
          error={errors.address}
          dataId={'address-input'}
          onChange={handleModelChange('address')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputFormElement
          label="Schätzwert"
          inputComponent={CurrencyNumberFormat}
          inputProps={{inputProps: {allowNegative: false}}}
          value={model.value}
          error={errors.value}
          dataId={'value-input'}
          onChange={handleModelChange('value')}
          required={!viewOnly}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <CalendarFormElement
          label="Bewertungsdatum"
          disableFuture
          value={model.valuation_date}
          error={errors.valuation_date}
          dataId={'value_date-input'}
          onChange={handleModelChange('valuation_date')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <SelectFormElementV2
          label="Art der Nutzung"
          value={model.usage}
          options={USAGE_OPTIONS}
          onChange={handleModelChange('usage')}
          dataId={'usage-input'}
          custom_classes={inputClasses}
          disabled={viewOnly}
          addEmptyOption
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputFormElement
          label="Kaufpreis"
          inputComponent={CurrencyNumberFormat}
          inputProps={{inputProps: {allowNegative: false}}}
          value={model.purchase_price}
          error={errors.purchase_price}
          dataId={'purchase_amount-input'}
          onChange={handleModelChange('purchase_price')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <CalendarFormElement
          label="Erwerbsdatum"
          value={model.acquisition_date}
          error={errors.acquisition_date}
          onChange={handleModelChange('acquisition_date')}
          dataId={'acquisition_date-input'}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <CalendarFormElement
          label="Baujahr"
          disableFuture
          yearOnly
          value={model.date_of_construction}
          error={errors.date_of_construction}
          onChange={handleModelChange('date_of_construction')}
          dataId={'date_of_construction-input'}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputFormElement
          label="Größe"
          inputComponent={SquareSizeNumberFormat}
          inputProps={{inputProps: {allowNegative: false}}}
          value={model.size}
          error={errors.size}
          onChange={handleModelChange('size')}
          dataId={'size-input'}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <SelectFormElementV2
          label={PAYMENT_FREQ_LABEL}
          value={currentPaymentFrequency}
          options={PAYMENT_FREQUENCY_OPTIONS}
          onChange={handleModelChange('payment_frequency')}
          dataId={'payment_frequency-input'}
          custom_classes={inputClasses}
          disabled={viewOnly || isUsageHasNoIncome}
          addEmptyOption
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputFormElement
          label={NET_INCOME_LABEL}
          inputComponent={CurrencyNumberFormat}
          inputProps={{inputProps: {allowNegative: false}}}
          value={currentNetIncome}
          error={errors.net_income}
          dataId={'net_income-input'}
          onChange={handleModelChange('net_income')}
          disabled={viewOnly || isUsageHasNoIncome}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Grid container spacing={1}>
          {/*FIXME: DISABLED FOR NOW as part of BCA-10671*/}
          {/*{!viewOnly && (*/}
          {/*  <Grid item xs={'auto'}>*/}
          {/*    <AutoCalculateToggle*/}
          {/*      value={model.is_auto_calculation}*/}
          {/*      handleValueChanged={handleModelChange('is_auto_calculation')}*/}
          {/*      disabled={isUsageHasNoIncome || !isAutoCalcAvailable}*/}
          {/*      disableReason={!isAutoCalcAvailable && AUTO_CALC_DISABLED_INFO}*/}
          {/*    />*/}
          {/*  </Grid>*/}
          {/*)}*/}
          <Grid item xs>
            <InputFormElement
              label="Jahresnettomieteinkünfte"
              inputComponent={CurrencyNumberFormat}
              inputProps={{inputProps: {allowNegative: false}}}
              value={currentRentalIncome}
              error={errors.rental_income}
              dataId={'rental_income-input'}
              onChange={handleModelChange('rental_income')}
              disabled={viewOnly || isUsageHasNoIncome || model.is_auto_calculation}
              custom_classes={inputClasses}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sm={6}>
        <SelectFormElementV2
          label="Zustand"
          value={model.condition}
          options={CONDITION_OPTIONS}
          onChange={handleModelChange('condition')}
          dataId={'condition-input'}
          custom_classes={inputClasses}
          disabled={viewOnly}
          addEmptyOption
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <CalendarFormElement
          label="Stand vom"
          value={model.updated || moment()}
          dataId={'updated-input'}
          disabled
          required={!viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      {!!errors['non_field_errors'] && <NonFieldErrors errors={errors['non_field_errors']}/>}
    </>
  )
};

ImmobilienForm.defaultProps = {
  defaults: {},
  errors: {}
};

export default connect()(ImmobilienForm);